import { useMemo } from 'react'

import { normalizeNetworkName } from '~publish/helpers/channels'

import type { ChannelLegendData } from '../../components/ChannelLegendItem'
import { getChannelColor } from '../../components/charts'
import type { ChannelTimeSeries } from '../hooks/useAllChannelsTimeSeries'

/** The metrics the All-Channels chart can plot per channel over time. */
export type MetricType = 'followers' | 'posts'

// Channels drawn before the user applies the page-level channel filter.
const DEFAULT_VISIBLE_CHANNELS = 5

// Ceiling once filtered: one channel per CHANNEL_COLORS entry, so every series
// keeps a distinct colour. Beyond this the set is truncated. Exported so the
// page caps the time-series fetch to the most channels the charts can draw.
export const MAX_CHART_CHANNELS = 10

function metricValue(ch: ChannelTimeSeries, metric: MetricType): number {
  const values = ch.primary[metric].flatMap((p) =>
    p.value == null ? [] : [p.value],
  )
  if (metric === 'followers') return values.at(-1) ?? 0
  return values.reduce((sum, value) => sum + value, 0)
}

interface UseMetricChannelsOptions {
  channelTimeSeries: ChannelTimeSeries[]
  metric: MetricType
  /** Total channels the account could show — drives the "showing N of M" notice. */
  totalChannelCount: number
  /** Whether a page-level channel filter is active (raises the display cap). */
  hasChannelFilter?: boolean
}

interface UseMetricChannelsResult {
  /** Channels with data for the metric, ranked by it (most first), capped to the display limit. */
  displayedChannels: ChannelTimeSeries[]
  /** Legend/colour data aligned by index with `displayedChannels`. */
  channels: ChannelLegendData[]
  /**
   * Channel id → its palette slot (index in the shared `channelTimeSeries` feed).
   * The colour authority: `channels[i].color` is `CHANNEL_COLORS[thisIndex]`, and
   * the new charts palette resolves the same hue from `getChannelColor(thisIndex)`.
   * Pass this (not the displayed-order index) so a filtered-out leading channel
   * doesn't shift every colour.
   */
  paletteIndexById: Map<string, number>
  /** Services present in the data but dropped for lacking the metric (e.g. GBP has no followers). */
  excludedServiceNames: string[]
  /** True when fewer channels are shown than the account has — show the notice. */
  showChannelCountNotice: boolean
}

/**
 * Shared channel derivation for the All-Channels metric cards (Followers, Posts).
 *
 * Filters channels to those with data for the metric, caps them to the display
 * limit, and assigns each a colour. Colours are keyed by channel id from the
 * *shared* `channelTimeSeries` order (not the per-metric filtered index), so a
 * channel keeps the same colour across both cards even when one metric excludes
 * a channel the other includes.
 */
export function useMetricChannels({
  channelTimeSeries,
  metric,
  totalChannelCount,
  hasChannelFilter = false,
}: UseMetricChannelsOptions): UseMetricChannelsResult {
  // Stable palette slot per channel id, assigned from the shared feed order (the
  // page caps this to MAX_CHART_CHANNELS, so indices stay within the palette).
  // Both cards read this, so a channel keeps its colour across them.
  const paletteIndexById = useMemo(() => {
    const map = new Map<string, number>()
    channelTimeSeries.forEach((ch, index) => {
      map.set(ch.channel.id, index)
    })
    return map
  }, [channelTimeSeries])

  const channelsWithData = useMemo(
    () =>
      channelTimeSeries.filter((ch) =>
        ch.primary[metric].some((p) => p.value != null),
      ),
    [channelTimeSeries, metric],
  )

  const displayLimit = hasChannelFilter
    ? MAX_CHART_CHANNELS
    : DEFAULT_VISIBLE_CHANNELS
  const displayedChannels = useMemo(
    () =>
      channelsWithData
        .map((ch) => ({ ch, value: metricValue(ch, metric) }))
        .toSorted((a, b) => b.value - a.value)
        .slice(0, displayLimit)
        .map(({ ch }) => ch),
    [channelsWithData, metric, displayLimit],
  )

  const channels = useMemo<ChannelLegendData[]>(
    () =>
      displayedChannels.map((ch) => ({
        id: ch.channel.id,
        name: ch.channel.name,
        service: ch.channel.service,
        color: getChannelColor(paletteIndexById.get(ch.channel.id) ?? 0),
      })),
    [displayedChannels, paletteIndexById],
  )

  // Services present in the data but dropped for lacking the selected metric, so
  // they can be named in an explanatory notice.
  const excludedServiceNames = useMemo(() => {
    const withDataIds = new Set(channelsWithData.map((ch) => ch.channel.id))
    const names = new Set<string>()
    for (const ch of channelTimeSeries) {
      if (!withDataIds.has(ch.channel.id)) {
        names.add(normalizeNetworkName(ch.channel.service))
      }
    }
    return [...names]
  }, [channelTimeSeries, channelsWithData])

  // The `> 0` guard avoids flashing "Showing 0 of N" while data loads.
  const showChannelCountNotice =
    displayedChannels.length > 0 && displayedChannels.length < totalChannelCount

  return {
    displayedChannels,
    channels,
    paletteIndexById,
    excludedServiceNames,
    showChannelCountNotice,
  }
}
